import { useMemo } from "react";

import moment from "moment";

import { InfoCircle } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { SecondaryButton } from "@components/v2/buttons";
import { useModelInsightsKPI } from "@features/mmm/hooks/mmm-url-state";
import { FETCH_MODEL_INSIGHTS_MODEL_FIT } from "@gql/mmm-model-insights";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchModelInsightsModelFit,
  type FetchModelInsightsModelFitVariables,
} from "@north-beam/nb-client-side";
import { formatting } from "@north-beam/nb-common";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useModelMetadata } from "@pages/mmm/hooks/use-model-metadata";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { exportModelFitToCsv } from "./model-fit-chart/utils/export-to-csv";

interface Props {
  onClickModalOpen: () => void;
}

export const ModelTitle = ({ onClickModalOpen }: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const { selectedCycle } = useSelectedCycle();
  const [selectedKpi] = useModelInsightsKPI();
  const { user } = useUser();
  const [modelMetadata] = useModelMetadata();

  const { data } = useNorthbeamQuery<
    FetchModelInsightsModelFit,
    FetchModelInsightsModelFitVariables
  >(FETCH_MODEL_INSIGHTS_MODEL_FIT, {
    variables: { cycleId: selectedCycle.id },
  });

  const modelFitData = useMemo(
    () =>
      selectedKpi?.kpi_name
        ? data?.me.mmm.modelInsightsModelFit[selectedKpi.kpi_name] ?? []
        : [],
    [data, selectedKpi],
  );

  const formatter = (value: number) => {
    if (selectedKpi?.kpi_type === "price_value") {
      return formatting.formatCurrency(value, {
        precision: "exact",
        code: user.currencies.currentCurrency,
      });
    }

    return formatting.formatInteger(value, { precision: "exact" });
  };

  const csvFileName = `${selectedKpi?.friendly_name} Model Fit`;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-xl">
          Model Fit: {selectedKpi?.friendly_name}
        </div>
        <div className="flex items-center space-x-2">
          <SecondaryButton
            className="flex items-center justify-center gap-2"
            onClick={() => {
              trackMMMEvent({
                component:
                  ModelInsightsTrackedComponent.EXPORT_MODEL_FIT_BUTTON,
                action: "onClick",
              });

              exportModelFitToCsv(csvFileName, modelFitData, formatter);
            }}
          >
            <i className="fa-regular fa-download text-cosmic" />
            EXPORT MODEL FIT
          </SecondaryButton>
          <SecondaryButton onClick={() => onClickModalOpen()}>
            MODEL TRAINING DETAILS
          </SecondaryButton>
        </div>
      </div>
      <p className="text-sm max-w-[70%]">
        Model fit is a representation of how well the model predicts your
        selected KPI. The model has been trained on the ‘In Sample’ data and
        then predicts what the ‘Out of Sample’ (your latest historical data)
        values should be. Accuracy is determined by how well the model predicted
        your actual performance in the ‘Out of Sample’ period.
      </p>
      <div className="flex items-center space-x-4 text-xs">
        <div>
          <span className="text-gray+1 mr-1">Model training period:</span>
          <span className="font-semibold">
            {moment(modelMetadata?.training_start_date).format("MMM DD, YYYY")}{" "}
            - {moment(modelMetadata?.training_end_date).format("MMM DD, YYYY")}
          </span>
          <span onClick={onClickModalOpen} className="cursor-pointer">
            <InfoCircle />
          </span>
        </div>
        <div>
          <span className="text-gray+1 mr-1">Trained on:</span>
          <span className="font-semibold">
            {(modelMetadata?.spend_data_columns.length ?? 0) +
              (modelMetadata?.promo_data_columns.length ?? 0) +
              (modelMetadata?.indicator_data_columns.length ?? 0)}{" "}
            features
          </span>
          <span onClick={onClickModalOpen} className="cursor-pointer">
            <InfoCircle />
          </span>
        </div>
      </div>
    </div>
  );
};
